// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useRef, useEffect, useState } from 'react';
import { Typography, Link } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { Link as RouterLink } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReCAPTCHA from 'react-google-recaptcha';
import HubspotForm from 'react-hubspot-form';
import { CreateOrgThunk } from 'Features/admin/adminThunk';
import PayAsYouGoCard from '../PayAsYouGoCard';
import stylesDef from './styles';
import { useAppDispatch } from '../../../hooks';

const HUBSPOT_PORTAL_ID = '3434755';
const HUBSPOT_FORM_ID = 'd9f1edf9-5ff0-43be-bfa8-a48f1112a337';
// Shown when the request itself blew up; server-side failures carry their own message
const GENERIC_SIGNUP_ERROR = 'An error has been encountered. Please contact AirWorks.';

// This component renders signup form
// It renders on "/signUp" route, parent component is auth(index.tsx)
const SignUp = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const captchaRef = useRef(null);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  // Bumped after a failed attempt: HubSpot's embed shows its own thank-you state once it has submitted,
  // so a new key re-mounts an empty form the user can fill in again
  const [formInstance, setFormInstance] = useState(0);

  useEffect(() => {
    // this is to use hubspot callbacks without jQuery
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, []);

  // A failed attempt: show why, then send the user back through the captcha to a fresh form
  const showFailure = (message: string) => {
    setErrorMessage(message);
    setDisableSubmit(true);
    setFormInstance((instance) => instance + 1);
  };

  // hubspot handler on event listener
  // Registered once, so it only ever calls state setters and never reads component state
  const handler = async (event: MessageEvent) => {
    // Callback that executes after form is validated, just before the data is actually sent.
    // The redirect to the success page happens inside CreateOrgThunk, only when our API accepted the signup
    if (event.data.type !== 'hsFormCallback' || event.data.eventName !== 'onFormSubmit' || event.data.id !== HUBSPOT_FORM_ID) {
      return;
    }
    setErrorMessage('');
    try {
      const obj = {};
      // loop through the actual data that was sent to hubspot
      for (let i = 0; i < event.data.data.length; i += 1) {
        const newData = Object.values(event.data.data[i]);
        const entries = new Map([newData]);
        Object.assign(obj, Object.fromEntries(entries));
      }
      // rename hubspot object keys to our keys for eg: rename firstname --> firstName...etc
      const renameKey = (ob: any, old_key: string, new_key: string) => {
        // check if old key = new key
        if (old_key !== new_key) {
          Object.defineProperty(
            ob,
            new_key,
            Object.getOwnPropertyDescriptor(ob, old_key),
          );
          delete ob[old_key];
        }
      };

      const keys = Object.keys(obj);
      keys.forEach((key) => {
        if (key === 'firstname') renameKey(obj, 'firstname', 'firstName');
        if (key === 'lastname') renameKey(obj, 'lastname', 'lastName');
        if (key === 'company') renameKey(obj, 'company', 'companyName');
        if (key === 'phone') renameKey(obj, 'phone', 'phoneNumber');
        if (key === 'jobtitle') renameKey(obj, 'jobtitle', 'jobTitle');
        if (key === 'n2023_industry') renameKey(obj, 'n2023_industry', 'industry');
      });

      obj.captchaToken = await captchaRef.current.getValue();
      // call create org admin on submit button click
      const result = await dispatch(CreateOrgThunk(obj));
      if (!result.success) {
        showFailure(result.message || GENERIC_SIGNUP_ERROR);
      }
    } catch {
      showFailure(GENERIC_SIGNUP_ERROR);
    } finally {
      captchaRef.current.reset();
    }
  };

  const handleCaptchaChange = () => {
    if (captchaRef.current.getValue()) {
      // User has successfully verified, so show the form
      setDisableSubmit(false);
    } else {
      // ReCAPTCHA expired or verification failed, so hide the form
      setDisableSubmit(true);
    }
  };
  return (
    <Box sx={styles.layout}>
      <Box sx={styles.formColumn}>
        <Typography variant="h5" sx={[styles.details, !disableSubmit && styles.signupForm]}>
          Verify to Sign up
        </Typography>
        <ReCAPTCHA
          sitekey={_GOOGLE_CAPTCHA_SITE_KEY_}
          onChange={handleCaptchaChange}
          ref={captchaRef}
        />
        {errorMessage && (
          <Box sx={styles.errorBanner} role="alert">
            <ErrorOutlineIcon fontSize="small" />
            <Typography variant="body2" sx={styles.errorBannerText}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        <Box sx={disableSubmit && styles.signupForm}>
          <HubspotForm
            key={formInstance}
            portalId={HUBSPOT_PORTAL_ID}
            formId={HUBSPOT_FORM_ID}
          />
        </Box>
        <Typography sx={styles.loginPrompt} variant="body1">
          Already have an account?
          {' '}
          <Link component={RouterLink} sx={styles.loginLink} to="/">
            Log in
          </Link>
        </Typography>
      </Box>
      <Box sx={styles.cardColumn}>
        <PayAsYouGoCard />
      </Box>
    </Box>
  );
};

export default SignUp;
